import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCopyEmail } from "@/hooks/useCopyEmail";
import { cn } from "@/lib/utils";
import type { CopyableEmailProps, CopyEmailButtonProps } from "@/types/ui/copyable-email";

export const CopyEmailButton = ({ email }: CopyEmailButtonProps) => {
  const { copied, copy } = useCopyEmail(email);

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 align-middle text-gray-500 hover:bg-genie-primary/10 hover:text-genie-primary focus-visible:ring-genie-primary"
      aria-label={`Copy email address ${email}`}
      title={copied ? "Email copied" : "Copy email address"}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void copy();
      }}
    >
      {copied ? <Check aria-hidden="true" className="text-genie-primary" /> : <Copy aria-hidden="true" />}
    </Button>
  );
};

/** Non-email fallbacks remain plain text; links and copy actions are siblings. */
export const CopyableEmail = ({ email, fallback = "—", className, textClassName, href }: CopyableEmailProps) => {
  const value = email?.trim();
  const address = value?.match(/^[^<>]*<([^<>]+)>$/)?.[1]?.trim() || value;
  if (!address || !/^[^<>\s@,;]+@[^<>\s@,;]+\.[^<>\s@,;]+$/.test(address)) {
    return <span className={className}>{email || fallback}</span>;
  }

  return (
    <span className={cn("inline-flex max-w-full min-w-0 items-center align-middle", className)}>
      {href ? (
        <a href={href} className={cn("min-w-0 break-all hover:underline", textClassName)}>{email}</a>
      ) : (
        <span className={cn("min-w-0 break-all", textClassName)}>{email}</span>
      )}
      <CopyEmailButton email={address} />
    </span>
  );
};
